import React, { useEffect, useState } from 'react';
import api from '../services/api';
import StatusBadge from '../components/shared/StatusBadge';
import AppreciationForm from '../components/tutor/AppreciationForm';

const fmt = d => d ? new Date(d).toLocaleDateString('fr-FR') : '?';

const CRITERES_LABELS = {
  assiduite:     'Assiduité et ponctualité',
  integration:   'Intégration dans l\'équipe',
  initiative:    'Initiative et autonomie',
  tenue:         'Tenue professionnelle',
  qualite:       'Qualité du travail fourni',
  consignes:     'Respect des consignes',
  communication: 'Communication',
};

const NIVEAU_LABELS = { 1: 'Très insuffisant', 2: 'Insuffisant', 3: 'Satisfaisant', 4: 'Très satisfaisant' };
const NIVEAU_COLORS = {
  1: 'bg-red-100 text-red-700',
  2: 'bg-orange-100 text-orange-700',
  3: 'bg-blue-100 text-blue-700',
  4: 'bg-green-100 text-green-700',
};

export default function TutorPage() {
  const [conventions, setConventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get('/tutor/conventions')
      .then(res => {
        const data = res.data;
        setConventions(data);
        // Auto-navigate si une seule convention signée
        const signed = data.filter(c => ['SIGNED', 'ARCHIVED'].includes(c.status));
        if (signed.length === 1 && !selected) {
          setSelected(signed[0].id);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (selected) {
    const convention = conventions.find(c => c.id === selected);
    return <ConventionView convention={convention} onBack={() => { setSelected(null); load(); }} />;
  }

  const signed = conventions.filter(c => ['SIGNED', 'ARCHIVED'].includes(c.status));
  const pending = conventions.filter(c => !['SIGNED', 'ARCHIVED'].includes(c.status));

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Mes stagiaires</h2>

      {loading ? (
        <div className="text-gray-500 text-sm">Chargement...</div>
      ) : conventions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          <p>Aucune convention associée à votre adresse email pour l'instant.</p>
          <p className="text-sm mt-2 text-gray-400">Votre email doit correspondre à celui renseigné dans la convention.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {signed.length > 0 && (
            <div className="space-y-3">
              {signed.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c.id)}
                  className="w-full text-left bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-gray-800">
                          {c.student?.firstName} {c.student?.lastName}
                        </h3>
                        <StatusBadge status={c.status} />
                        {c.appreciation && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Appréciation saisie
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{c.company?.name} · {c.classe?.name}</p>
                      <p className="text-sm text-gray-400">
                        {fmt(c.startDate)} → {fmt(c.endDate)}
                        {c.totalDays ? ` · ${c.totalDays} jours` : ''}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg">
                      Ouvrir
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {pending.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                En attente de signature complète
              </p>
              <div className="space-y-2">
                {pending.map(c => (
                  <div key={c.id} className="bg-gray-50 rounded-xl border border-gray-200 p-4 opacity-60">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">
                        {c.student?.firstName} {c.student?.lastName}
                      </span>
                      <StatusBadge status={c.status} />
                      <span className="text-xs text-gray-400">{fmt(c.startDate)} → {fmt(c.endDate)}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      L'appréciation sera disponible une fois la convention signée par tous les participants.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConventionView({ convention: initial, onBack }) {
  const [convention, setConvention] = useState(initial);
  const [tab, setTab] = useState('contact');

  // Présence
  const [stagiairePresent, setStagiairePresent] = useState(initial?.appreciation?.stagiairePresent ?? null);
  const [savingPresence, setSavingPresence] = useState(false);
  const [presenceSaved, setPresenceSaved] = useState(false);

  // Prise de contact
  const [disponibilites, setDisponibilites] = useState(initial?.appreciation?.disponibilites || '');
  const [contactPreference, setContactPreference] = useState(initial?.tutorContactPreference || '');
  const [savingContact, setSavingContact] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);

  const stageStarted = !convention.startDate || new Date(convention.startDate) <= new Date();

  const reload = () => {
    api.get(`/conventions/${convention.id}`)
      .then(res => {
        setConvention(res.data);
        const a = res.data.appreciation;
        setStagiairePresent(a?.stagiairePresent ?? null);
        setDisponibilites(a?.disponibilites || '');
        setContactPreference(res.data.tutorContactPreference || '');
      })
      .catch(console.error);
  };

  const savePresence = async () => {
    if (!stageStarted) return;
    setSavingPresence(true);
    setPresenceSaved(false);
    try {
      await api.post('/appreciations', {
        conventionId: convention.id,
        stagiairePresent,
        comment: convention.appreciation?.comment || '',
        criteresEvaluation: convention.appreciation?.criteresEvaluation || {},
        disponibilites: convention.appreciation?.disponibilites || '',
        tutorContactPreference: convention.tutorContactPreference || null,
      });
      setPresenceSaved(true);
      reload();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingPresence(false);
    }
  };

  const saveContact = async () => {
    setSavingContact(true);
    setContactSaved(false);
    try {
      await api.post('/appreciations', {
        conventionId: convention.id,
        disponibilites,
        tutorContactPreference: contactPreference || null,
        stagiairePresent: convention.appreciation?.stagiairePresent ?? null,
        comment: convention.appreciation?.comment || '',
        criteresEvaluation: convention.appreciation?.criteresEvaluation || {},
      });
      setContactSaved(true);
      reload();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingContact(false);
    }
  };

  const c = convention;
  const a = c.appreciation;
  const cls = c.classe;

  const TABS = [
    { id: 'contact', label: 'Prise de contact' },
    { id: 'appreciation', label: 'Appréciation du stagiaire' },
    { id: 'mission', label: 'Missions & Compétences' },
  ];

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">← Retour</button>
        <h2 className="text-xl font-semibold text-gray-800">
          {c.student?.firstName} {c.student?.lastName}
        </h2>
        <StatusBadge status={c.status} />
      </div>

      {/* Infos stage */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Étudiant</p>
          <p className="text-gray-800">{c.student?.firstName} {c.student?.lastName}</p>
          <p className="text-gray-500 text-xs">{c.student?.email}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Classe</p>
          <p className="text-gray-800">{cls?.name}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Entreprise</p>
          <p className="text-gray-800">{c.company?.name}</p>
          <p className="text-gray-500 text-xs">{c.company?.address}, {c.company?.city}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Période</p>
          <p className="text-gray-800">{fmt(c.startDate)} → {fmt(c.endDate)}</p>
          <p className="text-gray-500 text-xs">{c.totalDays} jours · {c.weeklyHours}h/semaine</p>
        </div>
      </div>

      {/* Présence du stagiaire */}
      {!stageStarted ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Présence du stagiaire</p>
          <p className="text-sm text-gray-500">
            La confirmation de présence sera disponible à partir du <strong>{fmt(c.startDate)}</strong>, premier jour du stage.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Le stagiaire s'est-il présenté au stage ?
          </p>
          <div className="flex items-center gap-3">
            {[{ val: true, label: 'Oui', cls: 'border-green-400 bg-green-50 text-green-700', sel: 'bg-green-500 text-white border-green-500' },
              { val: false, label: 'Non', cls: 'border-red-300 bg-red-50 text-red-700', sel: 'bg-red-500 text-white border-red-500' }]
              .map(({ val, label, cls, sel }) => (
                <button
                  key={String(val)}
                  type="button"
                  onClick={() => { setStagiairePresent(stagiairePresent === val ? null : val); setPresenceSaved(false); }}
                  className={`px-5 py-2 rounded-lg border font-medium text-sm transition-colors ${stagiairePresent === val ? sel : cls}`}
                >
                  {label}
                </button>
              ))}
            <button
              type="button"
              onClick={savePresence}
              disabled={savingPresence || stagiairePresent === null}
              className="ml-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {savingPresence ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            {presenceSaved && <span className="text-sm text-green-600">Enregistré</span>}
          </div>
          {stagiairePresent === false && (
            <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              Le stagiaire ne s'est pas présenté. Vous pouvez compléter les informations ci-dessous si nécessaire.
            </p>
          )}
        </div>
      )}

      {/* Onglets */}
      <div className="flex border-b border-gray-200 mb-4">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Onglet Prise de contact */}
      {tab === 'contact' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
          {/* Disponibilités */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Disponibilités pour un entretien
              <span className="text-gray-400 font-normal ml-1">(pour fixer/confirmer le rendez vous)</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Veuillez s'il vous plait indiquer vos créneaux disponibles pour un rendez vous avec l'enseignant resposnable du suivi de stage.
            </p>
            <textarea
              value={disponibilites}
              onChange={e => { setDisponibilites(e.target.value); setContactSaved(false); }}
              rows={3}
              placeholder={"Ex : Lundi et mercredi matin, de préférence après 9h\nJoignable au 06 xx xx xx xx"}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Préférence de contact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Préférence de contact
              <span className="text-gray-400 font-normal ml-1">(pour fixer/confirmer le rendez vous)</span>
            </label>
            <div className="flex gap-2">
              {['Téléphone', 'Email'].map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => { setContactPreference(contactPreference === opt ? '' : opt); setContactSaved(false); }}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    contactPreference === opt
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveContact}
              disabled={savingContact}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {savingContact ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            {contactSaved && <span className="text-sm text-green-600">Enregistré</span>}
          </div>

          {/* Récap si déjà rempli */}
          {(a?.disponibilites || c.tutorContactPreference) && (
            <div className="pt-4 border-t border-gray-100 space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Informations enregistrées</p>
              {a?.disponibilites && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Disponibilités</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{a.disponibilites}</p>
                </div>
              )}
              {c.tutorContactPreference && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Préférence de contact</p>
                  <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">{c.tutorContactPreference}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Onglet Missions & Compétences */}
      {tab === 'mission' && (
        <div className="space-y-4">
          {[
            { label: 'Missions confiées', value: c.missions || cls?.missionsDefaut },
            { label: 'Compétences visées', value: c.skills || cls?.competencesDefaut },
            { label: "Modalités d'évaluation", value: cls?.modalitesEvaluation },
            { label: 'Travail à fournir / livrables', value: cls?.travailAFournir },
          ].map(({ label, value }) => value ? (
            <div key={label} className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{value}</p>
            </div>
          ) : null)}
          {!c.missions && !cls?.missionsDefaut && !cls?.competencesDefaut && !cls?.modalitesEvaluation && !cls?.travailAFournir && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
              Aucune information disponible pour l'instant.
            </div>
          )}
        </div>
      )}

      {/* Onglet Appréciation */}
      {tab === 'appreciation' && (() => {
        const start = c.startDate ? new Date(c.startDate) : null;
        const end = c.endDate ? new Date(c.endDate) : null;
        const unlockDate = start && end ? new Date(start.getTime() + 0.7 * (end.getTime() - start.getTime())) : null;
        const appreciationLocked = unlockDate ? new Date() < unlockDate : false;

        return (
          <div className="space-y-4">
            {a && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <p className="text-xs font-medium text-gray-500">Appréciation enregistrée le {fmt(a.submittedAt)}</p>

                {a.criteresEvaluation && Object.keys(a.criteresEvaluation).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Évaluation par critères</p>
                    <div className="space-y-1.5">
                      {Object.entries(a.criteresEvaluation).map(([key, val]) => (
                        val ? (
                          <div key={key} className="flex items-center justify-between gap-4">
                            <span className="text-sm text-gray-700">{CRITERES_LABELS[key] || key}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${NIVEAU_COLORS[val]}`}>
                              {NIVEAU_LABELS[val]}
                            </span>
                          </div>
                        ) : null
                      ))}
                    </div>
                  </div>
                )}

                {a.comment && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Appréciation générale</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{a.comment}</p>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <AppreciationForm convention={c} onSaved={reload} locked={appreciationLocked} unlockDate={unlockDate} />
            </div>
          </div>
        );
      })()}
    </div>
  );
}
